import { Timestamp } from "firebase/firestore";
import serializable, { propretyCustomSerializable } from "../common/decorators/serializable";
import { EntityFirebase } from "../common/entity.firebase";
import moment, { Moment } from "moment";

export type PayableRecurrence = "none" | "weekly" | "monthly" | "yearly";
export type PayableStatus = "pending" | "paid" | "cancelled";

function advanceDueDate(from: Moment, recurrence: PayableRecurrence): Moment {
  const m = from.clone().startOf("day");
  switch (recurrence) {
    case "weekly":
      return m.add(1, "week");
    case "monthly":
      return m.add(1, "month");
    case "yearly":
      return m.add(1, "year");
    default:
      return m;
  }
}

@serializable
export class PayableBill extends EntityFirebase {
  @propretyCustomSerializable((value: Moment) => Timestamp.fromDate(value.toDate()))
  private dueDate: Moment;

  @propretyCustomSerializable((value: Moment | null) =>
    !value ? null : Timestamp.fromDate(value.toDate())
  )
  private paidAt: Moment | null;

  @propretyCustomSerializable((value: Moment | null) =>
    !value ? null : Timestamp.fromDate(value.toDate())
  )
  private lastPaidAt: Moment | null;

  @propretyCustomSerializable((value: Moment) => Timestamp.fromDate(value.toDate()))
  private createdAt: Moment;

  @propretyCustomSerializable((value: Moment) => Timestamp.fromDate(value.toDate()))
  private updatedAt: Moment;

  constructor(
    public readonly id: string,
    private title: string,
    private amount: number,
    dueDate: Moment,
    private payeeName: string,
    private payeeDocument: string | null,
    private category: string | null,
    private isRecurring: boolean,
    private recurrence: PayableRecurrence,
    private status: PayableStatus,
    paidAt: Moment | null,
    lastPaidAt: Moment | null,
    private notes: string | null,
    createdAt: Moment,
    updatedAt: Moment
  ) {
    super();
    this.dueDate = dueDate;
    this.paidAt = paidAt;
    this.lastPaidAt = lastPaidAt;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
  }

  getTitle() {
    return this.title;
  }
  setTitle(v: string) {
    this.title = v;
  }

  getAmount() {
    return this.amount;
  }
  setAmount(v: number) {
    this.amount = v;
  }

  getDueDate(): Moment {
    return this.dueDate;
  }
  setDueDate(v: Moment) {
    this.dueDate = v;
  }

  getPayeeName() {
    return this.payeeName;
  }
  setPayeeName(v: string) {
    this.payeeName = v;
  }

  getPayeeDocument() {
    return this.payeeDocument;
  }
  setPayeeDocument(v: string | null) {
    this.payeeDocument = v;
  }

  getCategory() {
    return this.category;
  }
  setCategory(v: string | null) {
    this.category = v;
  }

  getIsRecurring() {
    return this.isRecurring;
  }
  setIsRecurring(v: boolean) {
    this.isRecurring = v;
  }

  getRecurrence(): PayableRecurrence {
    return this.recurrence;
  }
  setRecurrence(v: PayableRecurrence) {
    this.recurrence = v;
  }

  getStatus(): PayableStatus {
    return this.status;
  }

  getPaidAt() {
    return this.paidAt;
  }

  getLastPaidAt() {
    return this.lastPaidAt;
  }

  getNotes() {
    return this.notes;
  }
  setNotes(v: string | null) {
    this.notes = v;
  }

  getCreatedAt() {
    return this.createdAt;
  }

  getUpdatedAt() {
    return this.updatedAt;
  }

  touchUpdated() {
    this.updatedAt = moment();
  }

  /** Quitação: única ou última parcela. */
  markPaidFully(now: Moment = moment()) {
    this.status = "paid";
    this.paidAt = now;
    this.touchUpdated();
  }

  /** Registra pagamento de conta recorrente e avança o próximo vencimento. */
  registerRecurringPayment(now: Moment = moment()) {
    if (!this.isRecurring || this.recurrence === "none") {
      this.markPaidFully(now);
      return;
    }
    this.lastPaidAt = now;
    this.dueDate = advanceDueDate(this.dueDate, this.recurrence);
    this.touchUpdated();
  }

  cancel() {
    this.status = "cancelled";
    this.touchUpdated();
  }

  isOverdue(now: Moment = moment()): boolean {
    if (this.status !== "pending") return false;
    return this.dueDate.clone().startOf("day").valueOf() < now.clone().startOf("day").valueOf();
  }

  static async build(id: string, data: Record<string, unknown>) {
    const dueRaw = data.dueDate as { toDate?: () => Date } | undefined;
    const paidRaw = data.paidAt as { toDate?: () => Date } | null | undefined;
    const lastPaidRaw = data.lastPaidAt as { toDate?: () => Date } | null | undefined;
    const createdRaw = data.createdAt as { toDate?: () => Date } | undefined;
    const updatedRaw = data.updatedAt as { toDate?: () => Date } | undefined;

    const recurrenceRaw = data.recurrence;
    const recurrence: PayableRecurrence =
      recurrenceRaw === "weekly" ||
      recurrenceRaw === "monthly" ||
      recurrenceRaw === "yearly" ?
        recurrenceRaw :
        "none";

    const statusRaw = data.status;
    const status: PayableStatus =
      statusRaw === "paid" || statusRaw === "cancelled" ? statusRaw : "pending";

    return new this(
      id,
      typeof data.title === "string" ? data.title : "",
      typeof data.amount === "number" ? data.amount : 0,
      dueRaw?.toDate ? moment(dueRaw.toDate()) : moment(),
      typeof data.payeeName === "string" ? data.payeeName : "",
      typeof data.payeeDocument === "string" ? data.payeeDocument : null,
      typeof data.category === "string" ? data.category : null,
      data.isRecurring === true,
      recurrence,
      status,
      paidRaw?.toDate ? moment(paidRaw.toDate()) : null,
      lastPaidRaw?.toDate ? moment(lastPaidRaw.toDate()) : null,
      typeof data.notes === "string" ? data.notes : null,
      createdRaw?.toDate ? moment(createdRaw.toDate()) : moment(),
      updatedRaw?.toDate ? moment(updatedRaw.toDate()) : moment()
    );
  }
}
